import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Linkedin, Search } from "lucide-react";
import { FormattedInsights } from "./insights/FormattedInsights";
import { InsightHeader } from "./insights/InsightHeader";
import { ProspectCard } from "./insights/ProspectCard";
import { NewsItems, SourceList, InsightReply, ReplyAppliedBanner, needsReply } from "./insights/InsightExtras";
import { ExecuteStrategyCTA, StrategyExecutedPanel } from "./insights/ExecuteStrategy";
import { SaveLeadDialog } from "./insights/SaveLeadDialog";
import { useProspectActions, useStrategyExecution } from "./insights/useInsightActions";

const EXECUTABLE_TYPES = ["strategy", "research", "competitor"];

const DetailedInsightsModal = ({ isOpen, onClose, activityData, userId, onStrategyExecuted }) => {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const markInteracted = () => setHasUserInteracted(true);
  const prospects = useProspectActions(userId, markInteracted);
  const strategy = useStrategyExecution(userId, activityData, onStrategyExecuted, markInteracted);

  if (!isOpen || !activityData) return null;

  const companiesFound = activityData.companiesFound || [];
  const newsItems = activityData.newsItems || [];
  const hasCompanyData = companiesFound.length > 0;
  const isProspect = activityData.type === "prospect";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      <Card className="relative w-full max-w-3xl max-h-[90vh] bg-[#0f1419] border-gray-800 overflow-hidden shadow-2xl">
        <InsightHeader activityData={activityData} userId={userId} onClose={onClose} />

        <ScrollArea className="h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {(!isProspect || !hasCompanyData) && activityData.summary && (
              <div className="rounded-lg border border-ism-teal/20 bg-ism-teal/5 p-4">
                <p className="text-white text-sm leading-relaxed">{activityData.summary}</p>
              </div>
            )}

            {isProspect && hasCompanyData && (
              <>
                <div>
                  <h3 className="text-sm font-bold text-[text-ism-teal] tracking-wide mb-4 flex items-center gap-2">
                    <Linkedin className="h-4 w-4" />
                    Prospects Found ({companiesFound.length})
                  </h3>
                  <div className="space-y-4">
                    {companiesFound.map((company, idx) => (
                      <ProspectCard
                        key={company.slug || company.url || `${company.companyName}-${company.personName || idx}`}
                        company={company}
                        idx={idx}
                        userId={userId}
                        actionId={activityData?.actionId}
                        {...prospects.cardProps(idx)}
                      />
                    ))}
                  </div>
                </div>
                <NewsItems items={newsItems} />
              </>
            )}

            {!isProspect && activityData.insights && (
              <>
                <FormattedInsights content={strategy.replyResult || activityData.insights} onInteraction={markInteracted} />
                <NewsItems items={newsItems} />
                <SourceList sources={activityData.sources || []} />
                {needsReply(activityData.insights) && !strategy.replyResult && (
                  <InsightReply text={strategy.replyText} setText={strategy.setReplyText} isReplying={strategy.isReplying} onSubmit={strategy.reply} />
                )}
                {strategy.replyResult && <ReplyAppliedBanner />}
                {EXECUTABLE_TYPES.includes(activityData.type) && (
                  <div className="mt-6">
                    {!strategy.results ? (
                      <ExecuteStrategyCTA type={activityData.type} executing={strategy.executing} enabled={hasUserInteracted} onExecute={strategy.execute} />
                    ) : (
                      <StrategyExecutedPanel results={strategy.results} activityData={activityData} userId={userId} onReset={strategy.resetResults} />
                    )}
                  </div>
                )}
              </>
            )}

            {isProspect && !hasCompanyData && (
              <div className="text-center py-8">
                <Search className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No prospects found in this search.</p>
                <p className="text-gray-500 text-sm mt-2">Try using Hunt mode for detailed lead generation.</p>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="sticky bottom-0 bg-[#0f1419] border-t border-gray-800 px-6 py-4">
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" className="flex-1 bg-transparent border-gray-700 text-white hover:bg-white/10">Close</Button>
          </div>
        </div>
      </Card>

      <SaveLeadDialog
        open={!!prospects.pendingSave}
        onOpenChange={(open) => !open && prospects.setPendingSave(null)}
        pending={prospects.pendingSave}
        onCancel={() => prospects.setPendingSave(null)}
        onConfirm={prospects.confirmSave}
      />
    </div>
  );
};

export default DetailedInsightsModal;
